import snakecase from 'lodash.snakecase'
import { generateFieldsByTableColumns } from '@/utils/functions'

/**
 * ============================================================================
 * DEPARTMENTS-SPECIFIC HELPERS
 * Business logic specific to departments module
 * ============================================================================
 */

/**
 * Build column configuration for departments
 * @param {Array} tableColumns - Table columns
 * @param {Array} originalColumns - Original columns config
 * @returns {Object} Columns configuration
 */
export function buildDepartmentsColumns (tableColumns, originalColumns) {
	return generateFieldsByTableColumns(tableColumns, originalColumns)
}

/**
 * Build sort configuration for departments
 * @param {Object} tableState - Table state with sortBy and sortOrder
 * @returns {Object} Sort configuration
 */
export function buildDepartmentsSort (tableState) {
	return {
		column: tableState.sortBy ? snakecase(tableState.sortBy) : null,
		order: tableState.sortOrder
	}
}

/**
 * Build departments query payload with filters
 * @param {Object} searchFilters - Search filters from state
 * @param {Array} companyIds - Company IDs filter
 * @returns {Object|null} Payload object or null
 */
export function buildDepartmentsPayload (searchFilters, companyIds = []) {
	let payload = null

	// Add company IDs filter
	if (companyIds.length) {
		payload = payload || {}
		payload.companyIds = companyIds
	}

	// Add search filters if they exist
	if (Object.keys(searchFilters).length > 2) {
		const { departmentIds } = searchFilters
		if (departmentIds) {
			payload = payload || {}
			payload.departmentIds = departmentIds
		}
	}

	return payload
}

/**
 * Update department in list
 * @param {Array} departments - Current departments array
 * @param {Object} updatedDepartment - Updated department data
 * @returns {Array} Updated departments array
 */
export function updateDepartmentInList (departments, updatedDepartment) {
	return departments.map(department =>
		department.id === updatedDepartment.id
			? { ...department, ...updatedDepartment }
			: department
	)
}

/**
 * Update partnership in list
 * @param {Array} partnerships - Current partnerships array
 * @param {Object} updatedPartnership - Updated partnership data
 * @returns {Array} Updated partnerships array
 */
export function updatePartnershipInList (partnerships, updatedPartnership) {
	return partnerships.map(partnership =>
		partnership.id === updatedPartnership.id
			? { ...partnership, ...updatedPartnership }
			: partnership
	)
}

/**
 * Add partnership to list
 * @param {Array} partnerships - Current partnerships array
 * @param {Object} newPartnership - New partnership data
 * @returns {Array} Updated partnerships array
 */
export function addPartnershipToList (partnerships, newPartnership) {
	return [...partnerships, newPartnership]
}

/**
 * Remove department from list
 * @param {Array} departments - Current departments array
 * @param {string} departmentId - Department ID to remove
 * @returns {Array} Updated departments array
 */
export function removeDepartmentFromList (departments, departmentId) {
	return departments.filter(department => department.id !== departmentId)
}

/**
 * Build partnerships payload based on department selection
 * @param {Object} user - Auth user object
 * @param {Array|null} departmentIds - Optional department IDs override
 * @returns {Object} Partnerships query payload
 */
export function buildPartnershipsPayload (user, departmentIds = null) {
	const payload = {}

	if (!departmentIds) {
		const selectedDepartmentId = user?.selectedDepartmentId
		payload.departmentIds = selectedDepartmentId || []
	} else {
		payload.departmentIds = departmentIds
	}

	return payload
}

/**
 * Build department search suggestions payload
 * @param {string} searchPhrase - Search phrase
 * @param {string|null} companyId - Company ID
 * @param {boolean} skipCompany - Whether to skip company filter
 * @returns {Object} Search payload
 */
export function buildDepartmentSearchPayload (searchPhrase, companyId, skipCompany = false) {
	const payload = { searchPhrase }

	if (companyId && !skipCompany) {
		payload.companyIds = companyId
	}

	return payload
}

/**
 * Format countries data for UI
 * @param {Array} countries - Raw countries data
 * @returns {Array} Formatted countries
 */
export function formatCountries (countries) {
	return countries?.map(country => ({
		id: country.code,
		name: country.name
	})) ?? []
}

/**
 * Update department debt status
 * @param {Object} department - Current department
 * @returns {Object} Updated department with cleared debt
 */
export function clearDepartmentDebt (department) {
	return {
		...department,
		debt: 0,
		shipmentsEnabled: true
	}
}
